import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronLeft, RefreshCw, Heart, AlertTriangle } from "lucide-react";

const SEGMENTS = [
  { id: "all", labelKey: "cpul.seg_all" },
  { id: "rising", labelKey: "cpul.seg_rising" },
  { id: "stable", labelKey: "cpul.seg_stable" },
  { id: "dropping", labelKey: "cpul.seg_dropping" },
  { id: "no_data", labelKey: "cpul.seg_no_data" },
];

export default function ClientPulse() {
  const t = useT();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/coach/client-pulse");
      setData(res.data);
    } catch (err) { toast.error(apiError(err, t("cpul.load_err"))); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    if (!user || user.role !== "coach") return;
    load();
  }, [user]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const counts = data?.counts || { dropping: 0, stable: 0, rising: 0, no_data: 0 };
  const clients = data?.clients || [];
  const filtered = filter === "all" ? clients : clients.filter((c) => (c.segment || c.status) === filter);

  return (
    <div>
      <Seo title={t("cpul.seo")} noindex />
      <Link to="/app/coach/circles" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="cp-back">
        <ChevronLeft className="h-4 w-4" /> {t("cir.title")}
      </Link>
      <PageHeader
        eyebrow={t("cpul.eyebrow")}
        title={t("cpul.title")}
        subtitle={t("cpul.subtitle")}
        action={
          <Button variant="outline" size="sm" className="gap-1.5 rounded-full" onClick={load} data-testid="cp-refresh">
            <RefreshCw className="h-4 w-4" /> {t("cpul.refresh")}
          </Button>
        }
      />

      {counts.dropping > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="cp-alert-banner">
          <AlertTriangle className="h-4 w-4" />
          {counts.dropping === 1 ? t("cpul.alert_one", { n: counts.dropping }) : t("cpul.alert_other", { n: counts.dropping })}
        </div>
      )}

      <div className="mb-5 flex gap-2 overflow-x-auto pb-1">
        {SEGMENTS.map((s) => {
          const n = s.id === "all" ? clients.length : (counts[s.id] ?? 0);
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setFilter(s.id)}
              data-testid={`cp-filter-${s.id}`}
              className={`shrink-0 rounded-full border px-3 py-1.5 text-sm transition-colors ${filter === s.id ? "border-primary bg-primary/5 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}
            >
              {t(s.labelKey)} · {n}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <Heart className="h-6 w-6" />
          </div>
          <p className="mt-4 font-display text-lg text-foreground">{t("cpul.all_good_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("cpul.all_good_desc")}</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {filtered.filter((c) => c.client_id).map((c) => (
            <Link key={c.client_id} to={`/app/coach/client/${c.client_id}`} data-testid={`cp-card-${c.client_id}`} className="block rounded-2xl border border-border bg-card p-4 transition-colors hover:bg-accent/40">
              <p className="font-semibold text-foreground">{c.client_name || c.client_email || t("ctl.client")}</p>
              <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Heart className="h-3 w-3 text-rose-500" /> {t("cpul.h7")} {c.avg_hearts_7d ?? "–"}</span>
                <span className="inline-flex items-center gap-1"><Heart className="h-3 w-3 text-rose-400" /> {t("cpul.h30")} {c.avg_hearts_30d ?? "–"}</span>
                {c.sessions != null && <span>{t("cpul.sessions")} {c.sessions}</span>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
